import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function Article() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20modern%20flat%20lay%20of%20an%20organized%20product%20research%20guide%20with%20category%20cards%2C%20seller%20profile%20sheets%20and%20QC%20photo%20printouts%20arranged%20on%20a%20bright%20clean%20desk%2C%20soft%20daylight%2C%20comprehensive%20buying%20guide%20concept%2C%20editorial%20top-down%20photography&width=1600&height=600&seq=article-hero-v2&orientation=landscape"
            alt="AllChinaBuy Spreadsheet Complete Guide"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>
        <div className="relative w-full px-4 md:px-6 lg:px-10 py-20 md:py-28">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-primary-500/80 text-background-50 text-xs font-medium rounded-full mb-4">
              Buying Guide • 2026 Edition
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4">
              AllChinaBuy Spreadsheet 2026 Complete Guide
            </h1>
            <p className="text-base md:text-lg text-background-100/90 max-w-2xl mx-auto">
              Your independent resource for understanding products, sellers, QC processes, shipping methods and everything you need to research before buying.
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-700">AllChinaBuy Spreadsheet Guide</span>
          </nav>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-8">
              The <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> has become an essential resource for anyone navigating the world of international product sourcing. This comprehensive guide explains what the spreadsheet is, how to use it effectively, and how to make informed purchasing decisions based on the information it provides.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-6">
              What Is the AllChinaBuy Spreadsheet?
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              The <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> is a structured, organized collection of product information designed to help buyers research items before making purchase decisions. Unlike random product listings or scattered forum posts, this spreadsheet consolidates categories, seller information, pricing references, and quality control data into one accessible format.
            </p>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              What makes this resource particularly valuable is its organizational approach. Products are grouped into logical categories — from sneakers and streetwear to accessories and lifestyle items — making it easy to find exactly what you are looking for without endless scrolling through unrelated listings.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-6">
              How to Navigate the Product Categories
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              The <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> organizes products into clear categories that mirror how buyers actually shop. Here is a breakdown of the main sections you will encounter:
            </p>

            {/* Category Table */}
            <div className="overflow-x-auto mb-8">
              <table className="w-full text-sm border border-background-200 rounded-lg">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 font-heading font-semibold text-foreground-800">Category</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold text-foreground-800">What to Expect</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold text-foreground-800">Research Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Shoes & Sneakers', 'Footwear across styles, brands, and price points', 'High — verify sizing and materials'],
                    ['Hoodies & Sweaters', 'Knitwear, pullovers, and layered clothing', 'Medium — check fabric weight and fit'],
                    ['T-Shirts', 'Basics, graphic tees, and seasonal releases', 'Medium — review print quality and cotton weight'],
                    ['Jackets & Outerwear', 'Coats, bombers, and seasonal pieces', 'High — check construction and lining details'],
                    ['Pants & Shorts', 'Denim, cargo, joggers, and tailored bottoms', 'Medium — verify measurements carefully'],
                    ['Headwear', 'Caps, beanies, and accessories', 'Low — generally straightforward sizing'],
                    ['Accessories', 'Bags, belts, jewelry, and lifestyle items', 'High — material authenticity varies widely'],
                    ['Perfumes', 'Fragrance listings and scent descriptions', 'High — impossible to verify without QC photos'],
                  ].map((row, i) => (
                    <tr key={i} className="border-t border-background-200">
                      <td className="px-4 py-3 font-medium text-foreground-800">{row[0]}</td>
                      <td className="px-4 py-3 text-foreground-600">{row[1]}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                          row[2].startsWith('High') ? 'bg-red-100 text-red-700' :
                          row[2].startsWith('Medium') ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {row[2].split(' — ')[0]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-6">
              Understanding Seller Information
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              One of the most important aspects of the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> is the seller data it contains. When you are looking at product listings, you are not just comparing prices — you are evaluating trustworthiness, reliability, and consistency.
            </p>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              Here is what experienced buyers recommend checking before committing to any seller found through the spreadsheet:
            </p>
            <ul className="space-y-3 mb-8">
              {[
                'How long the seller has been active in the marketplace',
                'Whether previous buyers have shared quality control photos',
                'If the seller specializes in specific product categories or brands',
                'Communication responsiveness and language clarity',
                'Return and exchange policies before purchase',
                'Payment method security and buyer protection options',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-check-line text-primary-500 mt-1 shrink-0" />
                  <span className="text-base text-foreground-600">{item}</span>
                </li>
              ))}
            </ul>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-6">
              QC Photos: What They Show and What They Cannot
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              Quality control (QC) photos are one of the most valuable tools available when using the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> for research. These images, typically taken in warehouse settings before shipment, give you a real-world look at the actual product you are considering.
            </p>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              However, it is important to understand the limitations. QC photos are taken under warehouse lighting, which can differ significantly from natural daylight. They may not capture every angle, and details like material texture, weight, or scent cannot be conveyed through images alone. Use QC photos as one tool in your research process, not as a definitive guarantee of quality.
            </p>

            <div className="bg-accent-50 border-l-4 border-accent-500 p-6 rounded-r-lg mb-8">
              <p className="text-sm text-accent-800 font-medium mb-2">Important Note</p>
              <p className="text-sm text-accent-700 leading-relaxed">
                No spreadsheet, photo, or guide can guarantee the authenticity, quality, or transaction outcome of any purchase. Always verify information independently and understand the risks involved in international buying.
              </p>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-6">
              Shipping Methods and Cost Considerations
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              Shipping is often the most misunderstood part of the buying process. The <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> helps you understand product costs, but shipping fees are typically calculated separately based on weight, destination, and chosen carrier.
            </p>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              Common shipping methods include express courier services for faster delivery with tracking, and postal services for more economical options. Factors that affect your total shipping cost include package weight and dimensions, destination country customs regulations, insurance options, and whether you choose to ship items individually or consolidate multiple purchases into one package.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-6">
              Warehouse Storage and Inspection
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              Most buying services offer warehouse storage, allowing you to accumulate multiple purchases before shipping them together. This consolidation can significantly reduce per-item shipping costs. During the warehouse phase, you typically have the opportunity to request additional QC photos, repackaging, or removal of unnecessary packaging to reduce weight.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-6">
              Safety Tips for First-Time Users
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              If you are new to using the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> or international buying in general, start with these foundational practices:
            </p>
            <ol className="space-y-4 mb-8 list-none">
              {[
                'Start with a small, low-value order to understand the full process before making larger purchases.',
                'Always verify seller URLs independently and never click suspicious links claiming to be the spreadsheet.',
                'Take screenshots of product listings, prices, and seller information at the time of purchase for your records.',
                'Join community forums where experienced buyers share tips and warnings about current market conditions.',
                'Understand your local customs regulations and potential import duties before ordering.',
                'Never share payment information or passwords through unofficial channels or messaging apps.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="w-7 h-7 shrink-0 flex items-center justify-center bg-primary-100 text-primary-600 rounded-full text-sm font-bold">
                    {i + 1}
                  </span>
                  <span className="text-base text-foreground-600 pt-0.5">{item}</span>
                </li>
              ))}
            </ol>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6 mb-12">
              {[
                {
                  q: 'Is the AllChinaBuy Spreadsheet updated regularly?',
                  a: 'The spreadsheet is maintained as an independent resource. Availability and update frequency depend on the community maintaining it. Always verify current information before making purchase decisions.',
                },
                {
                  q: 'Can I trust all sellers listed in the spreadsheet?',
                  a: 'No seller should be trusted blindly. The spreadsheet provides starting points for research. Always verify seller reputation, request QC photos, and use secure payment methods with buyer protection.',
                },
                {
                  q: 'What should I do if my order arrives with issues?',
                  a: 'Document everything with photos immediately. Contact your buying agent service first, as they typically handle dispute resolution. Keep all communication records and screenshots from your original purchase.',
                },
                {
                  q: 'How do I reduce shipping costs?',
                  a: 'Consolidate multiple items into one shipment, remove unnecessary packaging during warehouse inspection, choose slower shipping methods when time allows, and be aware of weight thresholds that trigger higher pricing tiers.',
                },
              ].map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-foreground-800 mb-2">{faq.q}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>

            {/* Final CTA */}
            <div className="bg-primary-50 rounded-lg p-8 md:p-10 text-center mt-12">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Ready to Start Researching?
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-xl mx-auto">
                Return to the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> homepage to explore categories, browse resources, and begin your product research journey.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/" className="px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
                  Back to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 text-sm font-semibold border-2 border-primary-500 text-primary-500 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
                >
                  Visit Main Website
                </a>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-12 pt-6 border-t border-background-200 text-center">
            <p className="text-xs text-foreground-500">
              Last Updated: August 26, 2026 • This is an independent informational resource
            </p>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}